import { useState, useCallback } from 'react';
import { useAuthContext } from './useAuthContext';
import { updateUserLocation } from '../services/firebase/user-profile';

/**
 * Custom hook for managing user location updates
 * @returns {Object} Hook state and methods
 */
export const useUserLocation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuthContext();

  /**
   * Update the user's location
   * @param {Object} locationDetails - Location details from LocationSearchFieldV2
   * @returns {Promise<boolean>} Success status
   */
  const updateLocation = useCallback(async (locationDetails) => {
    if (!user) {
      setError('User not authenticated');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await updateUserLocation(user.uid, locationDetails);
      
      if (result.success) {
        console.log('Location updated successfully:', result.data);
        return true;
      } else {
        setError(result.error || 'Failed to update location');
        return false;
      }
    } catch (err) {
      console.error('Error updating location:', err);
      setError(err.message || 'An unexpected error occurred');
      return false;
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Clear any error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    updateLocation,
    loading,
    error,
    clearError
  };
};

export default useUserLocation;